/**
 * Storage utilities for SSB App
 * Manages localStorage with schema versioning and migrations
 */

const STORAGE_KEY = 'SSB_APP_v1'
const CURRENT_SCHEMA_VERSION = 1

/**
 * Default state structure
 */
export const getDefaultState = () => ({
  meta: {
    schemaVersion: CURRENT_SCHEMA_VERSION,
    lastSaved: new Date().toISOString(),
  },
  user: {
    id: `local-${crypto.randomUUID()}`,
    displayName: 'Guest',
    settings: {
      mode: 'practice',
      theme: 'light',
      fontSize: 'medium',
      adsEnabled: false,
      analyticsOptIn: false,
    },
  },
  progress: {
    oir: { completed: 0, scores: [], attempts: [] },
    wat: { attempts: [], totalWords: 0 },
    tat: { stories: [], totalStories: 0 },
    srt: { responses: [], totalResponses: 0 },
    interview: { sessions: [] },
    tests: { history: [] },
  },
  drafts: {},
})

/**
 * Load state from localStorage
 * @returns {Object} The loaded state or default state
 */
export const loadState = () => {
  try {
    const serialized = localStorage.getItem(STORAGE_KEY)
    if (serialized === null) {
      const defaultState = getDefaultState()
      saveState(defaultState)
      return defaultState
    }
    const parsed = JSON.parse(serialized)
    // Apply migrations if needed
    return migrateState(parsed)
  } catch (err) {
    console.error('Error loading state from localStorage:', err)
    return getDefaultState()
  }
}

/**
 * Save state to localStorage
 * @param {Object} state - The state to save
 */
export const saveState = (state) => {
  try {
    const stateToSave = {
      ...state,
      meta: {
        ...state.meta,
        lastSaved: new Date().toISOString(),
      },
    }
    const serialized = JSON.stringify(stateToSave)
    localStorage.setItem(STORAGE_KEY, serialized)
  } catch (err) {
    console.error('Error saving state to localStorage:', err)
  }
}

/**
 * Clear all data from localStorage
 */
export const clearState = () => {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch (err) {
    console.error('Error clearing state from localStorage:', err)
  }
}

/**
 * Export state as JSON file
 * @param {Object} state - The state to export
 */
export const exportState = (state) => {
  try {
    const dataStr = JSON.stringify(state, null, 2)
    const dataBlob = new Blob([dataStr], { type: 'application/json' })
    const url = URL.createObjectURL(dataBlob)
    const link = document.createElement('a')
    link.href = url
    link.download = `ssb-backup-${new Date().toISOString().split('T')[0]}.json`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  } catch (err) {
    console.error('Error exporting state:', err)
    throw new Error('Failed to export data')
  }
}

/**
 * Import state from JSON file
 * @param {File} file - The file to import
 * @returns {Promise<Object>} The imported state
 */
export const importState = async (file) => {
  try {
    const text = await file.text()
    const imported = JSON.parse(text)
    
    // Validate schema version
    if (!imported.meta || typeof imported.meta.schemaVersion !== 'number') {
      throw new Error('Invalid backup file: missing schema version')
    }
    
    // Apply migrations if needed
    const migrated = migrateState(imported)
    return migrated
  } catch (err) {
    console.error('Error importing state:', err)
    throw new Error('Failed to import data: ' + err.message)
  }
}

/**
 * Migrate state to current schema version
 * @param {Object} state - The state to migrate
 * @returns {Object} The migrated state
 */
export const migrateState = (state) => {
  if (!state.meta || !state.meta.schemaVersion) {
    // Very old version or corrupted data
    return getDefaultState()
  }

  let migratedState = { ...state }
  const currentVersion = migratedState.meta.schemaVersion

  // Apply migrations sequentially
  if (currentVersion < CURRENT_SCHEMA_VERSION) {
    // Future migrations will go here
    // Example:
    // if (currentVersion < 2) {
    //   migratedState = migrateToV2(migratedState)
    // }
    
    migratedState.meta.schemaVersion = CURRENT_SCHEMA_VERSION
  }

  return migratedState
}

/**
 * Merge imported state with current state
 * @param {Object} currentState - Current state
 * @param {Object} importedState - Imported state
 * @param {string} strategy - 'replace' or 'merge'
 * @returns {Object} The merged state
 */
export const mergeStates = (currentState, importedState, strategy = 'merge') => {
  if (strategy === 'replace') {
    return importedState
  }

  // Merge strategy: keep user settings from current, merge progress data
  return {
    ...importedState,
    meta: {
      ...importedState.meta,
      lastSaved: new Date().toISOString(),
    },
    user: {
      ...currentState.user,
      id: currentState.user.id, // Keep current user ID
    },
    progress: {
      oir: {
        completed: Math.max(
          currentState.progress.oir?.completed || 0,
          importedState.progress.oir?.completed || 0
        ),
        scores: [
          ...(currentState.progress.oir?.scores || []),
          ...(importedState.progress.oir?.scores || []),
        ],
        attempts: [
          ...(currentState.progress.oir?.attempts || []),
          ...(importedState.progress.oir?.attempts || []),
        ],
      },
      wat: {
        attempts: [
          ...(currentState.progress.wat?.attempts || []),
          ...(importedState.progress.wat?.attempts || []),
        ],
        totalWords:
          (currentState.progress.wat?.totalWords || 0) +
          (importedState.progress.wat?.totalWords || 0),
      },
      tat: {
        stories: [
          ...(currentState.progress.tat?.stories || []),
          ...(importedState.progress.tat?.stories || []),
        ],
        totalStories:
          (currentState.progress.tat?.totalStories || 0) +
          (importedState.progress.tat?.totalStories || 0),
      },
      srt: {
        responses: [
          ...(currentState.progress.srt?.responses || []),
          ...(importedState.progress.srt?.responses || []),
        ],
        totalResponses:
          (currentState.progress.srt?.totalResponses || 0) +
          (importedState.progress.srt?.totalResponses || 0),
      },
      interview: {
        sessions: [
          ...(currentState.progress.interview?.sessions || []),
          ...(importedState.progress.interview?.sessions || []),
        ],
      },
      tests: {
        history: [
          ...(currentState.progress.tests?.history || []),
          ...(importedState.progress.tests?.history || []),
        ],
      },
    },
    drafts: {
      ...currentState.drafts,
      ...importedState.drafts,
    },
  }
}

/**
 * Get progress summary
 * @param {Object} state - The current state
 * @returns {Object} Progress summary statistics
 */
export const getProgressSummary = (state) => {
  const progress = state.progress || {}
  
  return {
    totalTests: progress.tests?.history?.length || 0,
    oirCompleted: progress.oir?.completed || 0,
    watAttempts: progress.wat?.totalWords || 0,
    tatStories: progress.tat?.totalStories || 0,
    srtResponses: progress.srt?.totalResponses || 0,
    interviewSessions: progress.interview?.sessions?.length || 0,
    lastActivity: state.meta?.lastSaved || null,
  }
}
